import { useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import Icon from "../../Icon/Icon";
import {
  CHART_AMBER,
  CHART_GRAY,
  CHART_GREEN,
  CHART_ORANGE,
  CHART_RED,
} from "../chartPalette";

// Color e icono del aviso según la severidad más alta de la lectura.
const SEVERIDAD = {
  critica: { color: CHART_RED, icon: "bold_warning_2" },
  alta: { color: CHART_ORANGE, icon: "bold_warning_2" },
  media: { color: CHART_AMBER, icon: "bold_warning_2" },
  baja: { color: CHART_GRAY, icon: "bold_info_circle" },
  ninguna: { color: CHART_GREEN, icon: "bold_info_circle" },
};

const Bullets = ({ items, color }) => (
  <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2.5 }}>
    {items.map((text, i) => (
      <Typography component="li" key={i} variant="body2" sx={{ color }}>
        {text}
      </Typography>
    ))}
  </Stack>
);

const Seccion = ({ title, children }) => (
  <Box>
    <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.6 }}>
      {title}
    </Typography>
    <Box sx={{ mt: 0.5 }}>{children}</Box>
  </Box>
);

/**
 * ScoreInsights — el aviso del Score Ético: un icono en el encabezado de la
 * tarjeta que abre la lectura completa del indicador.
 *
 * Todo lo que muestra son HIPÓTESIS sobre qué puede haber detrás del número,
 * nunca un diagnóstico cerrado; el diálogo lo dice explícitamente arriba.
 *
 * @param {Object} insights salida de `interpretar()` (ver `../scoreMessages`)
 */
export const ScoreInsights = ({ insights }) => {
  const [t] = useTranslation("dashboard");
  const theme = useTheme();
  const [open, setOpen] = useState(false);

  if (!insights) return null;

  const m = (key, opts) => t(`static_dashboard.ethical_messages.${key}`, opts);
  const lista = (key) => {
    const value = m(key, { returnObjects: true });
    return Array.isArray(value) ? value : [];
  };

  const { color, icon } = SEVERIDAD[insights.severidadMaxima] || SEVERIDAD.media;
  const base = `estados.${insights.estado}`;
  const causas = lista(`${base}.causas`);
  const acciones = lista(`${base}.acciones`);
  const { contexto } = insights;

  return (
    <>
      {/* EGIconButton resuelve `iconColor` contra la paleta del tema, así que
          un hex se caería a gris: aquí el color viene de la banda del gauge. */}
      <Tooltip title={m("open")} placement="top" arrow>
        <IconButton
          size="small"
          aria-label={m("open")}
          onClick={() => setOpen(true)}
          sx={{ flexShrink: 0 }}
        >
          <Icon name={icon} size={20} color={color} />
        </IconButton>
      </Tooltip>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth scroll="paper">
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="flex-start" spacing={1.5}>
            <Box sx={{ pt: 0.3 }}>
              <Icon name={icon} size={22} color={color} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="overline" sx={{ color, letterSpacing: 0.6 }}>
                {m(`severity_${insights.severidadMaxima}`)}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                {m(`${base}.titulo`)}
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography
              variant="caption"
              sx={{
                display: "block",
                p: 1.2,
                borderRadius: 1,
                bgcolor: theme.palette.action.hover,
                color: "text.secondary",
              }}
            >
              {m("disclaimer")}
            </Typography>

            <Typography variant="body2">{m(`${base}.diagnostico`)}</Typography>

            <Seccion title={m("reading")}>
              <Typography variant="body2">{m(`${base}.lectura`)}</Typography>
            </Seccion>

            <Typography variant="caption" color="text.secondary">
              {m("context", {
                percent: contexto.porcentajeDelReferente,
                observed: contexto.tasaObservada,
                benchmark: contexto.benchmark,
              })}
            </Typography>

            {contexto.provisional && (
              <Typography variant="caption" sx={{ color: CHART_ORANGE, fontWeight: 600 }}>
                {m("provisional")}
              </Typography>
            )}

            {causas.length > 0 && (
              <Seccion title={m("causes")}>
                <Bullets items={causas} color="text.primary" />
              </Seccion>
            )}

            {acciones.length > 0 && (
              <Seccion title={m("actions")}>
                <Bullets items={acciones} color="text.primary" />
              </Seccion>
            )}

            {insights.observaciones.length > 0 && (
              <>
                <Divider />
                <Seccion title={m("observations")}>
                  <Stack spacing={1.5} sx={{ mt: 0.5 }}>
                    {insights.observaciones.map((o) => {
                      const ob = `observaciones.${o.clave}`;
                      const obCausas = lista(`${ob}.causas`);
                      const obAcciones = lista(`${ob}.acciones`);
                      const tono = (SEVERIDAD[o.severidad] || SEVERIDAD.media).color;
                      return (
                        <Box
                          key={o.clave}
                          sx={{ borderLeft: `3px solid ${tono}`, pl: 1.5, py: 0.2 }}
                        >
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            {m(`${ob}.titulo`)}
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.3 }}>
                            {m(`${ob}.diagnostico`)}
                          </Typography>
                          {obCausas.length > 0 && (
                            <Box sx={{ mt: 0.8 }}>
                              <Bullets items={obCausas} color="text.secondary" />
                            </Box>
                          )}
                          {obAcciones.length > 0 && (
                            <Box sx={{ mt: 0.8 }}>
                              <Bullets items={obAcciones} color="text.secondary" />
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Stack>
                </Seccion>
              </>
            )}
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ScoreInsights;
